import { useEffect } from "react";
import variables from "@/config/variables";
import { useUser } from "@/contexts/UserContext/UserContext";
import { FinancialApplication } from "@/types/FinancialApplication";
import { HttpTransportType, HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import { useAtomValue, useSetAtom } from "jotai";
import { financialApplicationAtomFamily } from "@/atoms/financialApplicationAtomFamily";

// Singleton connection reference
let globalConnection: HubConnection | null = null;
let globalConnectionPromise: Promise<void> | null = null;
let isConnecting = false;

const isConnected = () => globalConnection?.state === "Connected";

interface SubscriptionState {
  count: number;
  handler: (id: string, payload: FinancialApplication) => Promise<void> | void;
}

const subscribers: Record<string, SubscriptionState> = {};

const handleReconnect = async () => {
  console.debug(`Reconnecting subscriptions for realtime listener`);
  for (const financialApplicationId in subscribers) {
    await globalConnection?.invoke("JoinFinancialApplication", financialApplicationId);
  }
};

const addSubscriber = async (
  financialApplicationId: string,
  handler: (id: string, payload: FinancialApplication) => Promise<void> | void
) => {
  if (!isConnected()) {
    console.warn(`Realtime connection not found.`);
    return;
  }

  if (subscribers[financialApplicationId]) {
    console.warn(
      `Subscriber for financial application ${financialApplicationId} already exists, incrementing count.`
    );
    const subscription = subscribers[financialApplicationId];
    subscription.count++;
    return;
  }

  console.debug(`Adding subscriber for financial application ${financialApplicationId}`);

  globalConnection?.on("FinancialApplicationUpdated", handler);

  subscribers[financialApplicationId] = {
    count: 1,
    handler,
  };

  await globalConnection?.invoke("JoinFinancialApplication", financialApplicationId);
};

const removeSubscriber = (financialApplicationId: string) => {
  if (!subscribers[financialApplicationId]) {
    console.warn(`Subscriber for financial application ${financialApplicationId} not found`);
    return;
  }

  if (!isConnected()) {
    console.warn(`Realtime connection not found.`);
    return;
  }

  console.debug(`Removing subscriber for financial application ${financialApplicationId}`);

  subscribers[financialApplicationId].count--;
  if (subscribers[financialApplicationId].count <= 0) {
    //globalConnection?.off("FinancialApplicationUpdated", subscribers[financialApplicationId].handler);
    //delete subscribers[financialApplicationId];
  }
};

export const useFinancialApplicationRealtime = (financialApplicationId: string) => {
  const { accessToken } = useUser();

  const financialApplicationState = useAtomValue(
    financialApplicationAtomFamily(financialApplicationId)
  );
  const setFinancialApplicationFromRemote = useSetAtom(
    financialApplicationState.setFinancialApplicationFromRemote
  );

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const ensureConnected = async () => {
      if (!accessToken) return;

      if (isConnected()) {
        return;
      }

      if (isConnecting && globalConnectionPromise) {
        await globalConnectionPromise;

        if (isConnected()) {
          return;
        }

        await ensureConnected();
        return;
      }

      isConnecting = true;
      globalConnectionPromise = (async () => {
        try {
          const setupGlobalConnection = async () => {
            globalConnection = new HubConnectionBuilder()
              .withUrl(`${variables.apiBaseUrl.replace(/\/$/, "")}/hubs/financial-application`, {
                accessTokenFactory: () => accessToken,
                withCredentials: true,
                transport: HttpTransportType.LongPolling,
              })
              .withKeepAliveInterval(1000)
              .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (_retryContext) => 1000,
              })
              .build();

            globalConnection.onreconnected(async () => {
              await handleReconnect();
            });

            globalConnection.onclose(async (reason) => {
              console.debug("Financial application realtime connection closed", reason);
              await setupGlobalConnection();
            });

            await globalConnection.start();
          };

          await setupGlobalConnection();
        } finally {
          isConnecting = false;
        }
      })();

      await globalConnectionPromise;
    };

    ensureConnected().then(() => {
      addSubscriber(financialApplicationId, async (id, payload) => {
        if (id?.toLowerCase() === financialApplicationId?.toLowerCase()) {
          await setFinancialApplicationFromRemote(payload, undefined);
        }
      });
    });

    return () => {
      console.debug(`Unmounting financial application ${financialApplicationId} realtime listener`);
      removeSubscriber(financialApplicationId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialApplicationId, accessToken, setFinancialApplicationFromRemote]);
};
